/**
 * Role-Helpers — gespiegelt aus src/StayOn.Infrastructure/Identity/Entities/RoleNames.cs.
 *
 * **Erinnerung**: UI-Visibility ist NUR Convenience. Server entscheidet final.
 * Niemals Frontend-Role-Check ohne Server-Authorization-Attribut!
 */
import { PRIVILEGE_CATALOG_VERSION, type AuthUser, type PrivilegeId } from '@stayon/auth-core';
import { CLEANING_QUEUE_SURFACE, DASHBOARD_SURFACE } from './privilegeSurfaces';

export const ROLES = {
  /** Technische Plattformverwaltung. Nur in einem Workspace vom Typ Platform wirksam. */
  PlatformAdmin: 'PlatformAdmin',
  /** Fachliche Verwaltung des eigenen Workspaces. */
  WorkspaceAdmin: 'WorkspaceAdmin',
  Staff: 'Staff',
  Housekeeping: 'Housekeeping',
  HousekeepingManager: 'HousekeepingManager',
  Owner: 'Owner',
  Guest: 'Guest',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

/** Workspace-Typ aus dem Token. Spiegelt WorkspaceKind. */
export const WORKSPACE_KIND_PLATFORM = 'Platform';

/**
 * Rollen-Gruppen — eine Quelle für Nav-Sichtbarkeit (AdminShell/Tabs) UND Route-Guards
 * (router `requireAuth`), damit beide nicht auseinanderdriften. Spiegelt die
 * Backend-Autorisierung (`RoleNames.*` in ApplicationRole.cs).
 *
 * `platformOnly` ist bewusst KEIN vollständiger Spiegel: die Server-Policy
 * `RequirePlatformAdmin` verlangt zusätzlich einen Workspace vom Typ Platform. Route-Guards
 * nutzen deshalb `isPlatformAdmin`, nicht diese Gruppe allein.
 */
export const ROLE_GROUPS: Record<
  | 'platformOnly'
  | 'workspaceAdmin'
  | 'workspaceMembers'
  | 'staff'
  | 'operations'
  | 'cleaningManagement'
  | 'operationsOwner'
  | 'staffOwner',
  Role[]
> = {
  platformOnly: [ROLES.PlatformAdmin],
  workspaceAdmin: [ROLES.WorkspaceAdmin],
  workspaceMembers: [
    ROLES.PlatformAdmin,
    ROLES.WorkspaceAdmin,
    ROLES.Staff,
    ROLES.Housekeeping,
    ROLES.HousekeepingManager,
    ROLES.Owner,
  ],
  staff: [ROLES.WorkspaceAdmin, ROLES.Staff],
  operations: [ROLES.WorkspaceAdmin, ROLES.Staff, ROLES.Housekeeping, ROLES.HousekeepingManager],
  cleaningManagement: [ROLES.WorkspaceAdmin, ROLES.Staff, ROLES.HousekeepingManager],
  // Spiegelt RoleNames.WorkspaceAdminStaffOwner — read-only-Flächen für Verwaltung und Owner.
  operationsOwner: [ROLES.WorkspaceAdmin, ROLES.Staff, ROLES.Owner],
  // Spiegelt RoleNames.WorkspaceAdminStaffOwner — Finance/Insights (kein Housekeeping).
  staffOwner: [ROLES.WorkspaceAdmin, ROLES.Staff, ROLES.Owner],
};

/**
 * Deutsche UI-Labels je Rolle. Die beiden Admin-Rollen müssen klar unterscheidbar sein —
 * „Admin" allein wäre für einen Kunden nicht mehr eindeutig. „Kunde" = Owner.
 */
export const ROLE_LABELS_DE: Record<Role, string> = {
  PlatformAdmin: 'Plattform-Administrator',
  WorkspaceAdmin: 'Workspace-Administrator',
  Staff: 'Mitarbeiter',
  Housekeeping: 'Reinigung',
  HousekeepingManager: 'Reinigungsleitung',
  Owner: 'Kunde',
  Guest: 'Gast',
};

export function hasRole(user: AuthUser | null, role: Role): boolean {
  return user?.roles.includes(role) ?? false;
}

export function hasAnyRole(user: AuthUser | null, ...roles: Role[]): boolean {
  if (!user) return false;
  return roles.some((r) => user.roles.includes(r));
}

/**
 * Nav-/Tab-Sichtbarkeit: ohne `roles`-Filter (undefined) für alle Authentifizierten
 * sichtbar, sonst nur wenn der User mindestens eine der erlaubten Rollen trägt.
 * Ein leeres Array sperrt bewusst alle (fail-closed), damit ein dynamisch
 * berechneter leerer Filter nicht versehentlich alles freigibt.
 * Der Server bleibt finale Instanz — das ist nur UI-Sichtbarkeit.
 */
export function canAccess(user: AuthUser | null, roles?: readonly Role[]): boolean {
  if (!user) return false;
  if (roles === undefined) return true;
  return hasAnyRole(user, ...roles);
}

/** Verwaltet den eigenen Workspace (Benutzer, Companies, Stripe, Steuer, Vorlagen). */
export function isWorkspaceAdmin(user: AuthUser | null): boolean {
  return hasRole(user, ROLES.WorkspaceAdmin);
}

/**
 * Spiegelt die Server-Policy `RequirePlatformWorkspace`: nur der Workspace-Typ, keine Rolle.
 * Fuer die Flaechen, die das ganze Channex-**Konto** zeigen — OTA-Konten-Uebersicht mit dem
 * kontoweiten Connect-Fenster. Das Mapping-Fenster eines Objekts oeffnet zusaetzlich eine
 * Support-Sitzung (`canOpenChannelMapping`); die operativen Flaechen leiten ihren Tenant aus dem
 * Token ab und brauchen die Bedingung nicht. Die Rolle prueft der Aufrufer zusaetzlich selbst.
 */
export function isPlatformWorkspace(user: AuthUser | null): boolean {
  return user?.workspaceKind === WORKSPACE_KIND_PLATFORM;
}

/**
 * Spiegelt die Server-Policy `RequirePlatformAdmin`: die Rolle allein reicht nicht, der
 * Workspace muss auch vom Typ Platform sein. Ohne die zweite Bedingung zeigte ein
 * PlatformAdmin in einem Customer-Workspace Flächen, die der Server mit 403 beantwortet.
 */
export function isPlatformAdmin(user: AuthUser | null): boolean {
  return hasRole(user, ROLES.PlatformAdmin) && user?.workspaceKind === WORKSPACE_KIND_PLATFORM;
}

/**
 * Write-/Verwaltungs-Rechte (Backend: alle Mutations = WorkspaceAdminAndStaff). Steuert das
 * Ein-/Ausblenden von Write-Controls in Views, die read-only-Rollen (Owner/Reinigung)
 * mitsehen. Server bleibt finale Instanz.
 */
export function canManage(user: AuthUser | null): boolean {
  return hasAnyRole(user, ...ROLE_GROUPS.staff);
}

export function canReadBookingPayments(user: AuthUser | null): boolean {
  return hasAnyRole(user, ...ROLE_GROUPS.staff);
}

/**
 * Spiegelt die Server-Eigenschaft `IUserContext.CanViewGuestPii`: Owner und Reinigung bekommen
 * Gastdaten serverseitig redigiert. Rein für die Beschriftung von Flächen gedacht — was
 * tatsächlich gelesen oder durchsucht wird, entscheidet ausschließlich der Server.
 */
export function canViewGuestPii(user: AuthUser | null): boolean {
  return can(user, 'Guests.OperationalContact.View') && can(user, 'Guests.PersonalData.View');
}

/**
 * Die sichtbare Oberfläche folgt ausschliesslich dem signierten Snapshot. Ein abweichender
 * Katalogstand ist absichtlich unsichtbar statt teilweise nach Rollen weiterzulaufen.
 */
export function can(user: AuthUser | null, privilege: PrivilegeId): boolean {
  return (
    user?.principalKind === 'operator' &&
    user.privilegeCatalogVersion === PRIVILEGE_CATALOG_VERSION &&
    user.privileges?.includes(privilege) === true
  );
}

export interface ShellCapabilities {
  objectScope: boolean;
  commandBar: boolean;
  inbox: boolean;
}

/**
 * Eine reine Cleaning-Queue erhält eine query-arme Arbeitsshell. Rein heißt ohne Operations-Rolle:
 * Admin, Staff und Owner erreichen über ihre Rolle Seiten mit Objektkontext, auch ohne Dashboard.
 * Der Objektkontext selbst folgt dem Objekt-Leserecht, denn er lädt die Objektliste.
 */
export function shellCapabilities(user: AuthUser | null): ShellCapabilities {
  const restrictedCleaningShell =
    can(user, CLEANING_QUEUE_SURFACE.privilege) &&
    !can(user, DASHBOARD_SURFACE.privilege) &&
    !hasAnyRole(user, ...ROLE_GROUPS.operationsOwner);
  const fullShell = !restrictedCleaningShell;
  return {
    objectScope: can(user, 'PropertyGroups.View'),
    commandBar: fullShell,
    inbox: fullShell,
  };
}

/**
 * Landing-Entscheidung für „/" (Dashboard). Das Dashboard-KPI-Backend ist WorkspaceAdmin/Staff-only,
 * darum bekommen scoped Rollen (Owner/Kunde, Reinigung) die Apartments-Liste als Landing.
 * Loop-sicher: leitet nur um, wenn der User `/properties` (operationsOwner) auch erreichen
 * darf — ein User ohne jede Operations-Rolle (z.B. reiner Gast) bleibt auf „/", statt in
 * einen Redirect-Loop `/` ↔ `/properties` zu geraten.
 */
export function shouldRedirectFromDashboard(user: AuthUser | null): boolean {
  return dashboardLanding(user) !== null;
}

/** Rollenfokussiertes Landing; Deep-Link-/API-Rechte bleiben davon unberuehrt. */
export function dashboardLanding(user: AuthUser | null): '/cleaning' | '/properties' | null {
  if (!user || can(user, DASHBOARD_SURFACE.privilege)) return null;
  if (can(user, CLEANING_QUEUE_SURFACE.privilege)) return '/cleaning';
  return hasAnyRole(user, ...ROLE_GROUPS.operationsOwner) ? '/properties' : null;
}
